'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { memo, useCallback } from 'react';
import { SEOLEIMRUN_LINKS } from 'src/app/(seoleimrun)/seoleimrun/_components/constants';
import { useSeoleimrunApply } from 'src/app/(seoleimrun)/seoleimrun/_components/useSeoleimrunApply';
import { useAppDispatch, useAppSelector } from 'src/store/client/config';
import { userActions } from 'src/store/client/slices/userSlice';
import { checkLogin } from 'src/utils/auth';
import customStorage from 'src/utils/storage';

import KottonseedLogo from './KottonseedLogo';
import type { Menu } from './MenuItem';
import MenuItem from './MenuItem';

const BASE_PATH = '/seoleimrun';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
} as const;

const drawerVariants = {
  hidden: { x: '100%', opacity: 0 },
  visible: { x: 0, opacity: 1 },
} as const;

const transition = {
  duration: 0.25,
  ease: 'easeInOut',
} as const;

const menuList: Menu[] = [
  { name: 'MY 설레임', link: { pathname: `${BASE_PATH}/my` }, auth: true },
  // 참가 신청 — 히어로 CTA 와 동일하게 접수 오픈 전 안내 모달. 페이지와 동일 상수 사용(드리프트 방지).
  { name: '설레임런 참가 신청', link: { pathname: SEOLEIMRUN_LINKS.apply } },
  { name: '설레임 선물하기', link: { pathname: `${BASE_PATH}/gift` } },
  { name: '구매 영수증 인증하기', link: { pathname: `${BASE_PATH}/receipt` } },
  { name: '설레는 경품뽑기', link: { pathname: `${BASE_PATH}/luckydraw` } },
  // category=설레임 → qna/write 에서 "설레임런" 문의유형 고정 선택. 페이지와 동일 상수 사용(드리프트 방지).
  { name: '1:1 문의하기', link: { pathname: SEOLEIMRUN_LINKS.inquiry } },
];

// 로그아웃 — 네비게이션이 아닌 액션 메뉴. 로그인 상태에서만 목록 맨 아래에 노출.
const LOGOUT_MENU: Menu = { name: '로그아웃', action: 'logout' };

interface DrawerMenuProps {
  isOpen: boolean;
  setOpen: () => void;
}

const DrawerMenu = memo(({ isOpen, setOpen }: DrawerMenuProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => state.user.LoggedIn);
  const handleApply = useSeoleimrunApply();

  const handleLogout = useCallback(async () => {
    try {
      await signOut({ redirect: false });
      customStorage.removeItem('user');
      dispatch(userActions.logout());
      router.push(BASE_PATH);
    } catch (error) {
      console.error('signOut Error', error);
    }
  }, [dispatch, router]);

  const handleMenuClick = useCallback(
    (menu: Menu) => {
      // 로그아웃 — 네비게이션이 아닌 액션 메뉴
      if (menu.action === 'logout') {
        handleLogout();
        return;
      }
      if (!menu.link) return;
      // 참가 신청 — 접수 오픈 전 안내 모달. 모달이 드로어에 가리지 않도록 먼저 닫는다.
      if (menu.link.pathname === SEOLEIMRUN_LINKS.apply) {
        setOpen();
        handleApply();
        return;
      }
      // 로그인 필요 메뉴 — 미로그인 시 로그인 후 복귀
      if (menu.auth && !checkLogin()) {
        router.push(`/account/login?retUrl=${encodeURIComponent(menu.link.pathname)}`);
        return;
      }
      // 외부 링크는 새 창
      const isExternal =
        menu.link.pathname.startsWith('http://') || menu.link.pathname.startsWith('https://');
      if (isExternal) {
        window.open(menu.link.pathname, '_blank', 'noopener,noreferrer');
        return;
      }
      router.push(menu.link.pathname);
    },
    [router, handleLogout, handleApply, setOpen],
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="설레임런 메뉴"
          className="fixed top-0 left-1/2 z-999 h-screen w-full max-w-[480px] -translate-x-1/2 overflow-hidden"
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-[3px] will-change-[backdrop-filter]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={transition}
            onClick={setOpen}
          />

          <motion.div
            className="absolute top-0 right-0 z-1 flex h-dvh w-[280px] flex-col bg-seoleimrun-brand-01"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={transition}
          >
            {/* 헤더 — 닫기 버튼 */}
            <div className="flex h-seoleimrun-gnb w-full shrink-0 items-center justify-end border-b-[0.5px] border-seoleimrun-primary-10 pr-5">
              <button
                type="button"
                onClick={setOpen}
                aria-label="메뉴 닫기"
                className="flex size-8 cursor-pointer items-center justify-center"
              >
                <CloseIcon />
              </button>
            </div>

            {/* 메뉴 리스트 + 하단 코튼시드 로고 */}
            <div className="flex flex-1 flex-col justify-between overflow-y-auto pt-4 pb-6 pl-8">
              <ul className="flex w-full flex-col gap-4">
                {menuList.map((menu) => (
                  <MenuItem key={menu.name} menu={menu} onClick={handleMenuClick} />
                ))}
                {isLoggedIn && <MenuItem menu={LOGOUT_MENU} onClick={handleMenuClick} />}
              </ul>
              <KottonseedLogo />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
});

DrawerMenu.displayName = 'DrawerMenu';

export default DrawerMenu;

// 메뉴 닫기 X 아이콘
function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M7 8L24 24" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M24.5 8L7.5 24" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
